import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Landmark, Sparkles, Flame, TriangleAlert } from "lucide-react";
import { SPIRITS, RANK_FILTERS, localizeSpirits } from "../data/goetia";
import { RitualSeal } from "../components/RitualSeal";
import { Reveal } from "../components/Reveal";
import { FilterBar } from "../components/goetia/FilterBar";
import { SpiritCard } from "../components/goetia/SpiritCard";
import { CompareTray } from "../components/goetia/CompareTray";
import { HierarchyMap } from "../components/goetia/HierarchyMap";
import { whatsappLink } from "../config/site";
import { useLang } from "../config/i18n";

const MODERN_ICONS = [Landmark, Sparkles, Flame];

const HistoryBlock = ({ title, children }) => (
  <div className="p-7 sm:p-8 rounded-xl bg-[#121216] border border-[#22222B]">
    <h4 className="font-heading text-base tracking-[0.12em] uppercase text-[#C5A059] mb-4">{title}</h4>
    <div className="space-y-3 text-sm sm:text-[15px] text-[#A3A3AF] leading-relaxed font-light">{children}</div>
  </div>
);

export default function GoetiaPage() {
  const [filter, setFilter] = useState("todos");
  const [query, setQuery] = useState("");
  const [compare, setCompare] = useState([]);
  const { lang, t } = useLang();
  const g = t.goetia;

  useEffect(() => {
    document.title = lang === "en"
      ? "72 Spirits of the Goetia | Kings, Dukes, Marquesses, Presidents and Sigils"
      : "72 Espíritos da Goétia | Reis, Duques, Marqueses, Presidentes e Sigilos";
    const meta = document.querySelector('meta[name="description"]');
    meta?.setAttribute(
      "content",
      lang === "en"
        ? "Discover the 72 spirits of the Ars Goetia, their hierarchies, traditionally associated attributes, depictions and sigils within the tradition of ceremonial magic and Western occultism."
        : "Conheça os 72 espíritos da Ars Goetia, suas hierarquias, atributos tradicionalmente associados, representações e sigilos dentro da tradição da magia cerimonial e do ocultismo ocidental."
    );
    return () => {
      document.title = "Mestre Jonas Barcellos | Alta Magia Goétia Luciferiana e Consultoria Espiritual";
    };
  }, [lang]);

  const localized = useMemo(() => localizeSpirits(lang), [lang]);

  const results = useMemo(() => {
    const f = RANK_FILTERS.find((r) => r.id === filter);
    const q = query.trim().toLowerCase();
    const matchSlugs = new Set(
      SPIRITS.filter((s) => f.match(s)).map((s) => s.slug)
    );
    return localized.filter((s) => {
      if (!matchSlugs.has(s.slug)) return false;
      if (!q) return true;
      const hay = [s.name, s.alt, String(s.n), s.ranks.join(" "), s.description, s.attributes.join(" "), s.appearance].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [filter, query, localized]);

  const toggleCompare = (slug) => {
    setCompare((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : prev.length < 3 ? [...prev, slug] : prev
    );
  };

  return (
    <div className="bg-[#0A0A0C]">
      <section data-testid="goetia-hero" className="relative min-h-[92vh] flex items-center justify-center overflow-hidden pt-[100px] pb-20">
        <RitualSeal className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(120vw,880px)] text-[#C5A059] opacity-[0.12] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(217,169,74,0.1),transparent_60%)] pointer-events-none" />
        <div className="absolute bottom-0 inset-x-0 h-64 bg-[linear-gradient(to_top,rgba(217,169,74,0.06),transparent)] pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <p className="text-[11px] font-semibold tracking-[0.3em] text-[#C5A059] uppercase mb-8">
              {g.heroOverline}
            </p>
            <p className="font-heading text-8xl sm:text-9xl gold-text font-light leading-none mb-4">72</p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#F7F7F8] tracking-wide mb-4">
              {g.heroTitle}
            </h1>
            <p className="text-sm sm:text-base text-[#C5A059] tracking-[0.12em] uppercase mb-10">
              {g.heroSubtitle}
            </p>
            <p className="text-[15px] sm:text-base text-[#D1D1DB] leading-relaxed font-light mb-6">
              {g.intro}
            </p>
            <div className="p-5 rounded-xl border border-[#22222B] bg-[#121216]/70 backdrop-blur text-left">
              <p className="text-xs sm:text-sm text-[#6E6E7D] leading-relaxed font-light">
                <span className="text-[#C5A059] font-semibold">{g.noteLabel}</span> {g.note}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <FilterBar filter={filter} setFilter={setFilter} query={query} setQuery={setQuery} />

      <section id="goetia-grid" data-testid="goetia-grid" className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase text-[#6E6E7D] mb-8" data-testid="goetia-results-count">
            {results.length} {results.length === 1 ? g.resultsOne : g.resultsMany}
          </p>
          {results.length === 0 ? (
            <div data-testid="goetia-empty" className="py-24 text-center">
              <p className="font-heading text-xl text-[#6E6E7D]">{g.emptyTitle}</p>
              <p className="text-sm text-[#6E6E7D] font-light mt-3">{g.emptyText}</p>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((s, i) => (
                <Reveal key={s.slug} delay={(i % 3) * 0.06}>
                  <SpiritCard spirit={s} inCompare={compare.includes(s.slug)} onToggleCompare={toggleCompare} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 sm:py-28 border-t border-[#22222B] bg-[#0D0D10]">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center mb-14">
            <p className="text-[11px] font-semibold tracking-[0.3em] text-[#C5A059] uppercase mb-5">{g.mapOverline}</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#F7F7F8] leading-snug">{g.mapTitle}</h2>
          </Reveal>
          <HierarchyMap onSelect={setFilter} />
        </div>
      </section>

      {g.rankSections.map(({ id, title, text }) => {
        const f = RANK_FILTERS.find((r) => r.id === id);
        const list = localized.filter((s) => SPIRITS.find((raw) => raw.slug === s.slug && f.match(raw)));
        return (
          <section key={id} data-testid={`rank-section-${id}`} className="py-16 sm:py-20 border-t border-[#22222B]">
            <div className="max-w-6xl mx-auto px-6">
              <Reveal className="max-w-3xl mb-10">
                <h2 className="font-heading text-2xl sm:text-3xl text-[#F7F7F8] uppercase tracking-wide mb-4">{title}</h2>
                <p className="text-sm sm:text-[15px] text-[#A3A3AF] leading-relaxed font-light">{text}</p>
              </Reveal>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {list.map((s, i) => (
                  <Reveal key={s.slug} delay={(i % 5) * 0.05}>
                    <Link
                      data-testid={`rank-chip-${s.slug}`}
                      to={`/goetia/${s.slug}`}
                      className="group block p-4 rounded-xl bg-[#121216] border border-[#22222B] hover:border-[#C5A059]/50 transition-colors duration-300 text-center"
                    >
                      <span className="block w-14 h-14 mx-auto rounded-full border border-[#C5A059]/35 bg-[#0D0D10] p-2 mb-3">
                        <img src={s.sigil} alt={`Sigilo de ${s.name}`} loading="lazy" className="w-full h-full object-contain sigil-gold" />
                      </span>
                      <span className="block text-[10px] tracking-[0.2em] text-[#6E6E7D] mb-1">{String(s.n).padStart(2, "0")}</span>
                      <span className="block font-heading text-sm text-[#F7F7F8] group-hover:text-[#C5A059] transition-colors uppercase tracking-wide">
                        {s.name}
                      </span>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      <section className="py-20 sm:py-28 border-t border-[#22222B] bg-[#0D0D10]">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="max-w-3xl mb-12">
            <p className="text-[11px] font-semibold tracking-[0.3em] text-[#C5A059] uppercase mb-5 flex items-center gap-2">
              <BookOpen size={14} /> {g.historyOverline}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#F7F7F8] leading-snug">{g.historyTitle}</h2>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2">
            {g.historyBlocks.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.06}>
                <HistoryBlock title={b.title}>
                  {b.p.map((par) => (
                    <p key={par.slice(0, 24)}>{par}</p>
                  ))}
                </HistoryBlock>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 border-t border-[#22222B]">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="max-w-3xl mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl text-[#F7F7F8] leading-snug">
              {g.modernTitle}
            </h2>
            <p className="mt-5 text-sm sm:text-[15px] text-[#A3A3AF] leading-relaxed font-light">
              {g.modernIntro}
            </p>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-3">
            {g.modernCards.map((card, i) => {
              const Icon = MODERN_ICONS[i];
              return (
                <Reveal key={card.title} delay={i * 0.08}>
                  <div className={`h-full p-7 rounded-xl bg-[#121216] border ${i === 0 ? "border-[#C5A059]/40" : "border-[#22222B]"}`}>
                    <Icon size={22} className="text-[#C5A059] mb-5" strokeWidth={1.4} />
                    <h4 className="font-heading text-sm tracking-[0.15em] uppercase text-[#F7F7F8] mb-3">{card.title}</h4>
                    <p className="text-sm text-[#A3A3AF] leading-relaxed font-light">{card.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <div data-testid="goetia-disclaimer" className="p-7 sm:p-8 rounded-xl bg-[#16161B] border border-[#D9A94A]/25 flex items-start gap-5">
              <TriangleAlert size={20} className="text-[#D9A94A] shrink-0 mt-0.5" strokeWidth={1.6} />
              <div>
                <h4 className="font-heading text-base tracking-[0.12em] uppercase text-[#F7F7F8] mb-3">{g.disclaimerTitle}</h4>
                <p className="text-sm text-[#A3A3AF] leading-relaxed font-light">
                  {g.disclaimerText}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="goetia-cta" className="relative py-24 sm:py-32 overflow-hidden border-t border-[#22222B]">
        <RitualSeal className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[620px] text-[#C5A059] opacity-[0.08] pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#F7F7F8] leading-snug mb-6">
              {g.ctaTitle1} <span className="gold-text">{g.ctaTitle2}</span>
            </h2>
            <p className="text-[15px] sm:text-base text-[#A3A3AF] leading-relaxed font-light mb-10">
              {g.ctaText}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button data-testid="goetia-cta-consulta" onClick={() => (window.location.href = "/#consulta")} className="btn-outline-gold">
                {g.ctaBtn1}
              </button>
              <a
                data-testid="goetia-cta-whatsapp"
                href={whatsappLink(g.ctaWa)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
              >
                {g.ctaBtn2}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <CompareTray compare={compare} setCompare={setCompare} />
    </div>
  );
}
